import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Preloader from '../../components/custom/preLoader';
import showErrorToast from '../../components/toast/toast.js';
import DashboardStatCard from '../../components/custom/DashboardStatCard';
import DashboardSpeciesStatCard from '../../components/custom/DashboardSpeciesStatCard';
import { useDashboardSuper } from '../../hooks/queries/useDashboardSuper';

function SpeciesHighlightCard({ highlight, colorIndex }) {
  const { fishTypeName, biggestFish, bestAvgWr, bestPsd } = highlight;
  const hasAny = biggestFish || bestAvgWr || bestPsd;

  if (!hasAny) return null;

  const lines = [];

  if (biggestFish) {
    lines.push(
      `Biggest (angling): L ${biggestFish.length ?? '—'} · W ${biggestFish.weight ?? '—'} · WR ${biggestFish.wr != null ? `${biggestFish.wr}%` : '—'}`
    );
    lines.push(
      [biggestFish.ownerName, biggestFish.pondName, biggestFish.caughtAt].filter(Boolean).join(' · ')
    );
  }

  if (bestAvgWr) {
    lines.push(
      `Best avg WR: ${bestAvgWr.avgWr}% (${bestAvgWr.sampleCount} fish) — ${[bestAvgWr.ownerName, bestAvgWr.pondName, bestAvgWr.sessionDate].filter(Boolean).join(' · ')}`
    );
  }

  if (bestPsd) {
    const psdParts = [
      bestPsd.psdT != null ? `T ${bestPsd.psdT}%` : null,
      bestPsd.psdM != null ? `M ${bestPsd.psdM}%` : null,
      bestPsd.psdP != null ? `P ${bestPsd.psdP}%` : null,
      bestPsd.psdQ != null ? `Q ${bestPsd.psdQ}%` : null,
    ].filter(Boolean);
    lines.push(`Best PSD: ${psdParts.join(' · ')}`);
    lines.push(
      [bestPsd.ownerName, bestPsd.pondName, bestPsd.sessionDate, bestPsd.nDenominator ? `n=${bestPsd.nDenominator}` : null]
        .filter(Boolean)
        .join(' · ')
    );
  }

  const primary = biggestFish
    ? `W: ${biggestFish.weight ?? '—'}`
    : bestAvgWr
      ? `WR: ${bestAvgWr.avgWr}%`
      : bestPsd
        ? `PSD-T: ${bestPsd.psdT}%`
        : '';

  return (
    <DashboardSpeciesStatCard
      colClass="col-lg-6 col-xl-4 mb-3"
      colorIndex={colorIndex}
      title={fishTypeName}
      primaryValue={primary}
      lines={lines}
    />
  );
}

const Dashboard = () => {
  const { data: stats = {}, isLoading, isError, error } = useDashboardSuper();

  useEffect(() => {
    if (!isError) return;
    const errorMessage = error?.response?.data?.message || 'Failed to load dashboard data';
    showErrorToast(errorMessage);
    console.error('Error fetching dashboard data:', error);
  }, [isError, error]);

  if (isLoading) {
    return <Preloader />;
  }

  const totalAccounts = stats.totalAccounts ?? stats.NumberOfAccounts ?? 0;
  const standardPackages = stats.standardPackageAccounts ?? 0;
  const multiLakePackages = stats.multiLakePackageAccounts ?? 0;
  const totalPondOwners = stats.totalPondOwners ?? totalAccounts;
  const paying = stats.packageRunningAccounts ?? stats.payingAccounts ?? 0;
  const trial = stats.trialAccounts ?? 0;
  const expired = stats.expiredAccounts ?? 0;
  const totalPonds = stats.totalPonds ?? stats.NumberOfPonds ?? 0;
  const avgPonds = stats.averagePondsPerAccount ?? 0;
  const totalPondManagers = stats.totalPondManagers ?? 0;
  const totalPondStaff = stats.totalPondManagementStaff ?? 0;
  const totalFishTypes = stats.totalFishTypes ?? stats.NumberOfFishTypes ?? 0;
  const mrr = stats.mrr != null ? Number(stats.mrr).toFixed(2) : '0.00';
  const arr = stats.arr != null ? Number(stats.arr).toFixed(2) : '0.00';
  const speciesHighlights = stats.speciesHighlights ?? [];

  return (
    <div>
      <ol className="breadcrumb float-xl-end">
        <li className="breadcrumb-item">
          <Link to="/dashboard">Home</Link>
        </li>
        <li className="breadcrumb-item active">
          <Link to="/dashboard">Dashboard</Link>
        </li>
      </ol>
      <h1 className="page-header">Dashboard</h1>

      <div className="row g-3 mb-4">
        <DashboardStatCard
          title="TOTAL ACCOUNTS"
          value={totalAccounts}
          icon="fa-users"
          colorIndex={0}
          to="/accounts"
        />
        <DashboardStatCard
          title="STANDARD PACKAGES"
          value={standardPackages}
          icon="fa-box"
          colorIndex={1}
          to="/accounts?subscription=running"
        />
        <DashboardStatCard
          title="MULTI-LAKE PACKAGES"
          value={multiLakePackages}
          icon="fa-layer-group"
          colorIndex={2}
          to="/accounts?subscription=running"
        />
        <DashboardStatCard
          title="TOTAL POND OWNERS"
          value={totalPondOwners}
          icon="fa-user-tie"
          colorIndex={3}
          to="/accounts"
        />
      </div>

      <div className="row g-3 mb-4">
        <DashboardStatCard
          title="TOTAL PONDS"
          value={totalPonds}
          icon="fa-globe"
          colorIndex={0}
          to="/pond"
        />
        <DashboardStatCard
          title="AVG PONDS / ACCOUNT"
          value={avgPonds}
          icon="fa-chart-bar"
          colorIndex={1}
          to="/pond"
        />
        <DashboardStatCard
          title="TOTAL POND MANAGERS"
          value={totalPondManagers}
          icon="fa-user-cog"
          colorIndex={2}
          to="/accounts"
        />
        <DashboardStatCard
          title="POND MGMT STAFF"
          value={totalPondStaff}
          icon="fa-user-friends"
          colorIndex={3}
          to="/accounts"
        />
      </div>

      <div className="row g-3 mb-4">
        <DashboardStatCard
          title="MRR"
          value={`$${mrr}`}
          icon="fa-dollar-sign"
          colorIndex={0}
          to="/packages"
        />
        <DashboardStatCard
          title="ARR"
          value={`$${arr}`}
          icon="fa-chart-line"
          colorIndex={1}
          to="/packages"
        />
        <DashboardStatCard
          title="ON TRIAL"
          value={trial}
          icon="fa-hourglass-half"
          colorIndex={2}
          to="/accounts?subscription=trial"
        />
        <DashboardStatCard
          title="FISH TYPES"
          value={totalFishTypes}
          icon="fa-fish"
          colorIndex={3}
          to="/fishTypes"
        />
      </div>

      {speciesHighlights.length > 0 ? (
        <section className="donezo-dashboard-section mb-4">
          <div className="donezo-dashboard-section__header">
            <h2 className="donezo-dashboard-section__title">Species highlights (angling only)</h2>
            <p className="donezo-dashboard-section__subtitle">
              Biggest fish, best session average WR (≥3 fish), and best PSD trophy % per species across the platform.
            </p>
          </div>
          <div className="row g-3">
            {speciesHighlights.map((h, index) => (
              <SpeciesHighlightCard key={h.fishTypeId || h.fishTypeName} highlight={h} colorIndex={index} />
            ))}
          </div>
        </section>
      ) : null}

      <div className="card border-0 shadow-sm">
        <div className="card-body">
          <h5 className="card-title mb-3">Subscription breakdown</h5>
          <div className="row text-center">
            <div className="col-6 col-md-3 mb-3 mb-md-0">
              <Link to="/accounts?subscription=running" className="text-decoration-none">
                <div className="fs-4 fw-bold text-teal">{paying}</div>
                <div className="small text-muted">Package running</div>
              </Link>
            </div>
            <div className="col-6 col-md-3 mb-3 mb-md-0">
              <Link to="/accounts?subscription=trial" className="text-decoration-none">
                <div className="fs-4 fw-bold text-indigo">{trial}</div>
                <div className="small text-muted">On trial</div>
              </Link>
            </div>
            <div className="col-6 col-md-3">
              <Link to="/accounts?subscription=expired" className="text-decoration-none">
                <div className="fs-4 fw-bold text-danger">{expired}</div>
                <div className="small text-muted">Expired</div>
              </Link>
            </div>
            <div className="col-6 col-md-3">
              <Link to="/accounts" className="text-decoration-none">
                <div className="fs-4 fw-bold text-dark">{totalAccounts}</div>
                <div className="small text-muted">Total accounts</div>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
